import type { MediaAsset } from '../../../api/assets';
import { mergeAssets } from '../../../api/assets';
import { uploadAllMedia } from '../../../api/media';
import { NOT_PUBLISHED } from '../../../api/managedRows';
import { publishLatest, storedDefinition } from '../../../state/livePage';
import { useProjects } from '../../../state/projects';
import { readPicks, type PickDeps } from '../attachments';

/**
 * A photo added from Your data → Live, made one of the SITE's photos (F10, deep-dive 2026-09-30).
 *
 * A gallery row names its photo (`src: 'first-dance.jpg'`) and the page resolves the name with
 * `S.media`, from the catalogue row's list — which only a publish writes. So the photo goes the
 * way a chat photo goes: re-encoded as a JPEG and named by the chat's rule (`readPicks`), never
 * over a photo the site has; uploaded under the catalogue id; recorded on the project with the
 * ones it has, read as STORED; and the page republished as a content update (an offline page
 * stays offline, a password page keeps its password).
 */
export async function uploadSitePhoto(input: {
  projectId: string;
  ownerId: string;
  file: File;
  /** The browser's decoder unless a test stands in for it. */
  toJpeg?: PickDeps['toJpeg'];
}): Promise<MediaAsset> {
  const { projectId, ownerId, file } = input;
  const before = await storedDefinition(projectId, ownerId);
  const catalogueId = before.hostedAppId;
  if (!catalogueId) throw new Error(NOT_PUBLISHED);

  const existing = (before.assets ?? []).map((a) => a.id);
  const { added, problems } = await readPicks([file], { picked: [], existing }, input.toJpeg ? { toJpeg: input.toJpeg } : {});
  const photo = added.find((p) => p.kind === 'photo');
  if (!photo || photo.kind !== 'photo') throw new Error(problems[0] ?? `${file.name} could not be added. Add a photo.`);

  const { uploaded } = await uploadAllMedia(catalogueId, [{ name: photo.name, contentType: 'image/jpeg', file: photo.file }]);
  const asset = uploaded[0];
  if (!asset) throw new Error(`${photo.name} did not upload. Try again.`);

  const now = await storedDefinition(projectId, ownerId);
  await useProjects.getState().updateProject(
    projectId,
    { definition: { ...now, assets: mergeAssets(now.assets ?? [], [{ id: asset.id, url: asset.url }]) } },
    ownerId,
  );
  try {
    await publishLatest(projectId, ownerId);
  } catch (e) {
    const why = e instanceof Error && e.message ? e.message : 'your page could not be updated.';
    throw new Error(`${asset.id} is one of your site’s photos now, but your page could not be updated to show it: ${why} Publish changes to try again.`);
  }
  return { id: asset.id, url: asset.url };
}
